import type { AppDefinition } from '@web/api/types'
import { InlineStatus } from '@web/components/InlineStatus'
import { MessageAvatar } from '@web/components/MessageAvatar'
import { TopBar } from '@web/components/mobile/TopBar'
import { SendButton } from '@web/components/SendButton'
import {
  DesktopSessionChrome,
  SessionHeading,
} from '@web/components/SessionHeading'
import {
  SessionControlsSkeleton,
  SessionWorkspaceSkeleton,
} from '@web/components/SessionSkeleton'
import { Icon } from '@web/components/ui/icons'
import { appConfiguration } from '@web/state/app-configuration'
import { handleComposerEnter } from '@web/state/enter-key-behavior'
import { useIdentity } from '@web/state/identity'
import { useEnterKeyBehavior } from '@web/state/local-enter-key-behavior'
import { type RefObject, useContext, useEffect, useRef } from 'react'
import '@web/components/pending-session.css'

/** Render the conversation immediately while its server identity and uploads are pending. */
export function PendingSessionView({
  root,
  modal,
  label,
  title,
  app,
  agentName,
  model,
  narrow,
  prompt,
  filenames,
  draft,
  onDraftChange,
  status,
  error,
  onRetry,
  onEdit,
}: {
  root: RefObject<HTMLDivElement | null>
  modal: boolean
  label: string
  title: string
  app?: AppDefinition
  agentName: string
  model?: string | null
  narrow: boolean
  prompt: string
  filenames: string[]
  draft: string
  onDraftChange: (value: string) => void
  status: string
  error: string
  onRetry: () => void
  onEdit: () => void
}) {
  const identity = useIdentity()
  const desktopChrome = useContext(DesktopSessionChrome)
  const behavior = useEnterKeyBehavior(
    identity.state?.current_user?.enter_key_behavior,
  )
  const ui = app ? appConfiguration(app).ui : null
  const workspace = ui?.mode === 'hybrid' && !ui.minimized
  const user = identity.state?.current_user
  const composer = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    composer.current?.focus()
  }, [])
  return (
    <div
      ref={root}
      role={modal ? 'dialog' : 'region'}
      aria-modal={modal || undefined}
      aria-label={label}
      tabIndex={-1}
      className={`optimistic-session-view app-session mode-${workspace ? 'hybrid' : 'chat'} ${narrow ? 'm-session' : ''}`}
    >
      {narrow ? (
        <TopBar title={title} subtitle={agentName} />
      ) : (
        <SessionHeading
          identity={
            <div
              className={`${desktopChrome ? '' : 'session-heading-copy '}min-w-0 flex-1`}
            >
              <h1 className="truncate text-[13.5px] font-semibold">{title}</h1>
              <p className="text-[11px] text-faint">
                {agentName}
                {model ? ` · ${model}` : ''}
              </p>
            </div>
          }
        >
          {null}
        </SessionHeading>
      )}
      <div
        className={`session-panes pane-${ui?.position ?? 'right'} tab-chat`}
        style={
          {
            '--app-pane-width': `${ui?.width_percent ?? 55}%`,
          } as React.CSSProperties
        }
      >
        <section className="session-chat" aria-label="Chat">
          <div className="thread-viewport flex flex-1 flex-col overflow-y-auto px-4 pt-4">
            <div className="mx-auto w-full max-w-3xl flex-1 pb-2">
              <div className="session-lifecycle-entry" aria-live="polite">
                <InlineStatus
                  icon={error ? 'warning' : 'loader'}
                  loading={!error}
                  failed={!!error}
                >
                  {error ? (
                    <>
                      <p role="alert">{error}</p>
                      <div className="mt-3 flex gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={onRetry}
                        >
                          Retry session
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={onEdit}
                        >
                          Edit message
                        </button>
                      </div>
                    </>
                  ) : (
                    <p className="font-medium">{status}</p>
                  )}
                </InlineStatus>
              </div>
              {(prompt || filenames.length > 0) && (
                <article
                  className="group flex items-start justify-end gap-2 py-2"
                  aria-label="Opening message"
                >
                  <div className="min-w-0 max-w-[85%] whitespace-pre-wrap break-words rounded-card rounded-tr-sm bg-message-accent px-4 py-2.5 text-[14px] text-accent-fg text-balance">
                    {prompt && <p>{prompt}</p>}
                    {filenames.length > 0 && (
                      <ul className="pending-session-files">
                        {filenames.map((name, index) => (
                          <li key={`${index}:${name}`}>
                            <Icon name="fileText" size={14} />
                            <span>{name}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <span className="mt-2 block text-[10.5px] text-accent-fg">
                      Pending
                    </span>
                  </div>
                  <MessageAvatar
                    actor={{
                      kind: 'user',
                      id: user?.id ?? 'user',
                      name: user?.name,
                      avatarUrl: user?.avatar_url,
                    }}
                  />
                </article>
              )}
            </div>
            <div className="sticky bottom-0 mx-auto mt-auto w-full max-w-3xl bg-app pb-3 pt-2">
              <div className="card flex flex-col">
                <textarea
                  ref={composer}
                  rows={2}
                  aria-label="Message agent"
                  placeholder="Message agent…"
                  value={draft}
                  onChange={(event) => onDraftChange(event.target.value)}
                  onKeyDown={(event) =>
                    handleComposerEnter(
                      event,
                      behavior,
                      onDraftChange,
                      () => {},
                    )
                  }
                  className="w-full resize-none bg-transparent px-4 py-3 text-[14px] outline-none placeholder:text-faint"
                />
                <div className="flex items-center justify-between gap-2 px-3 pb-3">
                  <span className="text-xs text-muted">
                    You can keep drafting while the session connects.
                  </span>
                  <SendButton
                    className="btn btn-primary btn-icon"
                    label="Send message"
                    disabled
                    onSend={() => {}}
                  >
                    <Icon name="send" size={16} />
                  </SendButton>
                </div>
              </div>
            </div>
          </div>
        </section>
        {workspace && (
          <aside className="session-ui" aria-label={`${app?.title} workspace`}>
            <SessionWorkspaceSkeleton title={app?.title} />
          </aside>
        )}
      </div>
      <footer className="pending-session-status">
        <span className="status-dot starting" />
        {error ? 'Connection needs attention' : 'Connecting'}
        <SessionControlsSkeleton />
      </footer>
    </div>
  )
}
